import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core'
import { map } from 'rxjs/operators'
import { Live, LiveOptionsViewerSegment, LiveReactionCount } from 'src/lib/entity/live'
import { Magazine } from 'src/lib/entity/magazine'
import { LiveUserAuthority } from 'src/lib/entity/live-user-authority'
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client'
import { ConsoleLogger } from 'src/lib/console-logger'
import { FirestoreAccess } from 'src/lib/firestore-access'
import { Observable } from 'rxjs'

type ActionKind = 'REACTION' | 'COMMENT' | 'GIFT' | 'ERROR'

@Component({
  selector: 'maglive-input-comment',
  templateUrl: './input-comment.component.html',
  styleUrls: ['./input-comment.component.scss'],
})
export class InputCommentComponent implements OnInit {
  private INPUT_Focus_Time = 10
  private ENTER_Key_Number = 13
  commentProcessing = false
  comment: string
  reactionGoodCount$: Observable<LiveReactionCount>

  @ViewChild('commentInput', { static: false })
  commentInput?: ElementRef<HTMLInputElement>

  @Input() live: Live
  @Input() magazine: Magazine | undefined
  @Input() authority: LiveUserAuthority | undefined
  @Input() isShowGiftConfirmComponent: boolean
  @Output() showGiftControlEvent = new EventEmitter<boolean | void>()
  @Output() showGiftConfirmControlEvent = new EventEmitter<boolean | void>()
  @Output() showLoginAlertEvent = new EventEmitter<string | void>()

  constructor(
    private logger: ConsoleLogger,
    private firestoreAccess: FirestoreAccess,
  ) {}

  ngOnInit(): void {
    if (this.live.id) {
      this.reactionGoodCount$ = this.firestoreAccess.getReactionGoodCount(this.live.id)
    }
  }

  /**
   * コメント投稿押下時にコメントができるか判定する
   */
  onCommentInputClick(live: Live) {
    const actionStatus = this.canAction(
      live.options.reactionSegment,
      this.authority
    )
    if (actionStatus !== 'OK') {
      this.alertActionError('COMMENT')
      return
    }
  }

  /**
   * コメント投稿押下時に投稿内容を精査しつつOKならaddCommentAPIを叩いて保存する
   */
  onCommentInputSubmit(live: Live, $event: KeyboardEvent = null) {
    if (!this.comment) return
    if (this.commentProcessing) return

    // Avoid crazy Safari behavior
    // See: https://qiita.com/ledsun/items/31e43a97413dd3c8e38e
    // noinspection JSDeprecatedSymbols
    if ($event !== null && $event.keyCode !== this.ENTER_Key_Number) return

    // コメント保存中フラグをONにしてdisableにする(連投防止)
    this.commentProcessing = true
    this.logger.info(
      this.constructor.name,
      'onCommentInputSubmit',
      'comment',
      this.comment
    )
    MagliveApiClient.addComment(live.id, this.comment)
      .pipe(
        map((x) => {
          if (x === 'NOT_LOGIN' || x === 'INSUFFICIENT_LIVE_CONDITION') {
            this.alertActionError('COMMENT')
          }
          if (x === 'ENTITY_TOO_LARGE') {
            alert('入力されたコメントが長すぎます。')
            return
          }
          if (x === 'UNACCEPTABLE_CONTENT') {
            alert('投稿できない内容が含まれています。')
            return
          }
          this.comment = ''
          setTimeout(() => {
            this.commentInput.nativeElement.focus()
          }, this.INPUT_Focus_Time)
        })
      )
      .subscribe(
        () => {
          this.commentProcessing = false
        },
        (error) => {
          this.commentProcessing = false
          console.error(error)
        }
      )
  }

  /**
   * いいねボタン押下時にpostLikeAPIを叩いて保存する
   */
  onLikeSubmit(live: Live) {
    const actionStatus = this.canAction(
      live.options.reactionSegment,
      this.authority
    )
    if (actionStatus !== 'OK') {
      this.alertActionError('REACTION')
      return
    }

    // TODO: 一瞬溜めてから送信
    MagliveApiClient.postLike(live.id, 1)
      .pipe(
        map((x) => {
          if (x === 'NOT_LOGIN') {
            this.alertActionError('REACTION')
          }
          if (x === 'INSUFFICIENT_LIVE_CONDITION') {
            this.alertActionError('ERROR')
          }
        })
      )
      .subscribe(
        () => {},
        (error) => {
          console.error(error)
        }
      )
  }

  /**
   * ギフトコンポーネントの表示切り替え(親へ送信)
   */
  showGiftComponent() {
    if (this.authority === undefined) {
      this.alertActionError('GIFT')
      return
    }
    // 確認画面が開いている時に押下された時の挙動
    if (this.isShowGiftConfirmComponent) {
      this.showGiftControlEvent.emit(false)
      this.showGiftConfirmControlEvent.emit(false)
    } else {
      this.showGiftControlEvent.emit()
      this.showGiftConfirmControlEvent.emit(false)
    }
  }

  /**
   * いいね・コメント投稿ができるか判定する
   * 仕様：ログインさえしていれば購読者じゃなくても実行可能
   */
  private canAction(
    segment: LiveOptionsViewerSegment | undefined,
    authority: LiveUserAuthority | undefined
  ) {
    if (authority === undefined) {
      return 'LOGIN'
    } else if (
      segment === 'ALL' ||
      segment === 'LOGIN' ||
      segment === 'SUBSCRIBER'
    ) {
      return 'OK'
    } else {
      return 'OK'
    }
  }

  /**
   * アラートコメントを出力する
   */
  private alertActionError(actionKind: ActionKind) {
    let actionText = ''
    if (actionKind === 'ERROR') {
      alert('エラーが発生しました')
      return
    } 
    if (actionKind === 'REACTION') actionText = 'いいね！機能'
    if (actionKind === 'COMMENT') actionText = 'コメント機能'
    if (actionKind === 'GIFT') actionText = 'ギフト機能'
    this.showLoginAlertEvent.emit(actionText)
  }
}
